import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, authHeaders } from "@/lib/api";
import { AI } from "@/constants/testIds";
import { toast } from "sonner";
import { Copy, RefreshCw, Loader2 } from "lucide-react";

export default function ThankYouDialog({ record, kind, open, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!record) return;
    setBusy(true);
    setText("");
    try {
      const { data } = await api.post(
        "/ai/thank-you",
        { kind, record_id: record.id, tone: "warm" },
        { headers: authHeaders() }
      );
      setText(data.text || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft a thank-you");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open && record) generate();
    if (!open) setText("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record?.id]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (e) {
      toast.error("Couldn't copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={AI.thankYouDialog} className="rounded-none bg-slate-950 border border-white/15 max-w-lg">
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-widest text-kinetic">// AI thank-you draft</div>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">
            For {record?.name} · ${Number(record?.amount || 0).toLocaleString()}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Review, tweak, paste into your email. The AI drafts a short note tailored to this backer.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[160px] bg-slate-900 border border-white/15 p-4 whitespace-pre-wrap text-sm text-slate-200">
          {busy && !text ? (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin text-kinetic" /> Drafting a thank-you…
            </div>
          ) : (
            text || <span className="text-slate-500">No draft yet.</span>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            onClick={generate}
            disabled={busy}
            variant="outline"
            className="rounded-none border-white/15 flex-1"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Regenerate
          </Button>
          <Button
            data-testid={AI.thankYouCopy}
            onClick={copy}
            disabled={!text}
            className="rounded-none bg-kinetic hover:bg-[#FF5E1A] text-black font-bold uppercase flex-1"
          >
            <Copy className="w-4 h-4 mr-2" /> Copy
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
